import React, { useEffect, useRef, useState } from 'react';
import PatientInfo from '../../PatientInfo';
import CreditCardForm, { validateCreditCardForm } from '../../payment/CreditCardForm';
import { scrollToMessage } from '../../util/scrollToMessage';
import { useCreditCardForm } from '../../hooks/useCreditCardForm';
import FieldLevelHelp from '../../components/FieldLevelHelp';
import { logEvent, transferPitlOrder } from '../../util/apiUtils';
import AlertMessage from '../../AlertMessage';

/**
 * Display the PITL Transfer screen, allowing the user to update patient information and submit
 * the Physician Fee payment.
 */
const PITLTransfer = ({ gearOrder, transferRequest, userInfo, setGearOrder, setLoading, setTransferRequest }) => {
	const [errorMessage, setErrorMessage] = useState(null);
	const { creditCardForm, setCreditCardForm, handleCreditCardFieldChange } = useCreditCardForm();
	const patientInfoRef = useRef();
	const pendingPayment = getPendingPayment(gearOrder);

	// Make sure we're scrolled to the top of the page when the component first mounts
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	// Clear the transferRequest, returning the user to the PITL Summary page
	const cancel = (event) => {
		event.preventDefault();
		setTransferRequest({ action: 'cancel' });
		logEvent(
			'{} - PITL Transfer cancelled. gearOrderId={}',
			'UI_ACTION_PITL_TRANSFER_CANCEL',
			gearOrder.id
		);
	};

	/**
	 * Validate PatientInfo and CreditCard forms, then submit the transfer request.
	 */
	const handleSave = (event) => {
		event.preventDefault();

		const validationPromises = [patientInfoRef.current.save()];

		if (!pendingPayment) {
			validationPromises.push(
				validateCreditCard({
					creditCardForm,
					gearOrderId: gearOrder.id,
					userType: userInfo.type,
					setCreditCardForm
				})
			);
		}

		setErrorMessage(null);
		setLoading(true);
		Promise.all(validationPromises)
			.then(([patientInfo, orderPayment]) => {
				const request = {
					gearOrderId: gearOrder.id,
					gearReqIds: transferRequest.gearReqIds,
					newPatient: patientInfo,
					physicianFeePayment: orderPayment
				};
				return transferPitlOrder(request, gearOrder.patientInfo.dateOfBirth);
			})
			.then((transferResponse) => {
				if (transferResponse.gearOrder) {
					setGearOrder(transferResponse.gearOrder);
				}

				if (transferResponse.error) {
					return Promise.reject(transferResponse.error);
				} else {
					setTransferRequest({
						action: 'success',
						transferredOrderId: transferResponse.transferredOrderId,
						partialTransfer: transferResponse.transferredOrderId !== gearOrder.id
					});
				}
			})
			.catch(handleError)
			.finally(() => {
				setLoading(false);
			});
	};

	/**
	 * Handle errors from a transfer response.
	 *
	 * ValidationErrors are handled by the PatientInfo or CreditCardForm components. They will display
	 * validation messages and we call scrollToMessage() to scroll to the first one.
	 *
	 * For all other errors, we display an error message and scroll to it.
	 */
	const handleError = (err) => {
		if (err === 'ValidationError') {
			scrollToMessage();
		} else {
			let errorMessage = '';
			// Payment exceptions might contain a message that we want to display to the user, like Credit Card Declined
			if (err?.error === 'RequisitionPaymentException') {
				errorMessage = err.message || '';
			}

			let errorEl;
			if (errorMessage) {
				errorEl = <span>{errorMessage}</span>;
			} else if (err?.error === 'RequisitionPaymentException') {
				errorEl = (
					<span>
						An unexpected error occurred when submitting your payment. Please review your details and try
						again or call us at 800-522-4762 or contact <a href="/contact">Customer Support</a> for help.
					</span>
				);
			} else {
				errorEl = (
					<span>
						An unexpected error occurred when submitting your order. Please try again, or call us at
						800-522-4762 or contact <a href="/contact">Customer Support</a> for help.
					</span>
				);
			}

			setErrorMessage(errorEl);

			// Scroll up to the error message
			scrollToMessage();
		}
	};

	/**
	 * patientInfoRef.current.save() will validate the form and call updatePatientInfo to do the actual save.
	 * By returning a promise with the updated details, we will be able use that in handleSave.
	 * Example: patientInfoRef.current.save().then((updatedPatientInfo) => { ... });
	 */
	const handlePatientInfoUpdate = (updatedOrder) => {
		return Promise.resolve(updatedOrder.patientInfo);
	};

	// Create a copy of the gearOrder to pass to PatientInfo so it can be edited
	const editableOrder = createEditableOrder(gearOrder, transferRequest.gearReqIds);

	/**
	 * The PatientInfo component gets passed in a few methods that aren't needed for the PITL transfer process.
	 * Use this no-op function that doesn't do anything.
	 */
	const noOp = () => {};

	return (
		<div className="space-top">
			<h3 className="title">Order #{gearOrder.id}</h3>
			<div
				style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}
			>
				<p>Update patient information and pay the $15 Physician Fee to transfer your pack(s).</p>
				<button
					className="btn btn-white radius btn-sm margin-clear"
					title="Return to your order"
					type="button"
					onClick={cancel}
				>
					<i className="pl-10 fa fa-arrow-circle-left"></i> &nbsp; Back
				</button>
			</div>

			<div className="confirmation-section mt-20">
				<h3 className="mt-0">Patient Information</h3>
				<PatientInfo
					editRef={(ref) => (patientInfoRef.current = ref)}
					triggerInlineSave={handleSave}
					userInfo={userInfo}
					gearOrder={editableOrder}
					setLoading={setLoading}
					updatePatientInfo={handlePatientInfoUpdate}
					setInsuranceOptions={noOp}
					doConfirm={noOp}
					setDirty={noOp}
					showError={noOp}
					setGearOrder={noOp}
					getWorkflowStep={noOp}
				/>
			</div>

			<div className="confirmation-section">
				<h3 className="mb-20 mt-0">Payment</h3>
				<PaymentTable reqsToTransfer={editableOrder.requisitions} />

				{!!pendingPayment ? (
					<div className="mb-10">
						Your payment has been saved.
						<br />
						<strong>Amount:</strong> ${pendingPayment.cardAuthAmount}
						<br />
						<strong>Transaction ID:</strong> {pendingPayment.transactionId}
					</div>
				) : (
					<CreditCardForm
						handlePaymentInputChange={handleCreditCardFieldChange}
						onSubmit={handleSave}
						paymentDetails={creditCardForm}
					/>
				)}
			</div>

			{errorMessage && (
				<div style={{ marginBottom: '40px' }}>
					<AlertMessage type="danger" additionalCssClass="mt-0">
						<strong className="validation-message">{errorMessage}</strong>
					</AlertMessage>
				</div>
			)}

			<div>
				<button
					className="btn btn-primary radius btn-sm margin-clear pull-right"
					type="submit"
					onClick={handleSave}
				>
					Submit
					<i className="pl-10 fa fa-arrow-circle-right"></i>
				</button>
				<button className="btn btn-white radius btn-sm margin-clear" type="button" onClick={cancel}>
					<i className="pl-10 fa fa-arrow-circle-left"></i> &nbsp; Back
				</button>
			</div>
		</div>
	);
};

/**
 * List the packs being transferred and show the Physician Fee
 */
const PaymentTable = ({ reqsToTransfer }) => {
	return (
		<table className="table table-striped">
			<thead>
				<tr>
					<th colSpan="2">Transfer Collection Pack</th>
				</tr>
			</thead>
			<tbody>
				{reqsToTransfer.map((req) => {
					return (
						<tr key={req.id}>
							<td colSpan="2">{req.omsOrderableItemName}</td>
						</tr>
					);
				})}
			</tbody>
			<tfoot>
				<tr>
					<td colSpan="2" className="text-right">
						<span className="">Physician Fee</span>
						<span onClick={() => window.open('https://connect.gdx.net/#faq', '_blank')}>
							<FieldLevelHelp
								textClassName="text-info"
								iconClassName="fa-info-circle"
								helpText="Fee covers physician authorization. Telehealth appointments to discuss results are available for an additional cost."
								trigger="hover"
								large={true}
								html={false}
							/>
						</span>
						<span style={{ minWidth: '150px', display: 'inline-block' }}>
							<span className="">$15.00</span>
						</span>
					</td>
				</tr>
				<tr>
					<td colSpan="2" className="text-right">
						<span>
							<strong>Total</strong>
						</span>
						<span style={{ minWidth: '150px', display: 'inline-block' }}>
							<strong>$15.00</strong>
						</span>
					</td>
				</tr>
			</tfoot>
		</table>
	);
};

/**
 * Validate the credit card form and return a Promise with the OrderPayment object.
 * Validation errors will be set in the creditCardForm state and rejected as 'ValidationError'.
 */
const validateCreditCard = ({ creditCardForm, gearOrderId, userType, setCreditCardForm }) => {
	return new Promise((resolve, reject) => {
		const validatedPaymentForm = validateCreditCardForm(creditCardForm);
		setCreditCardForm(validatedPaymentForm);
		if (Object.values(validatedPaymentForm).every((prop) => prop.valid)) {
			// Convert the form data into an OrderPayment object
			const orderPayment = {
				paymentType: 'pitlclientfee',
				paymentFrom: userType,
				cardNo: validatedPaymentForm.cardNo.value,
				cardCvv: validatedPaymentForm.cardCvv.value,
				cardExpDate: validatedPaymentForm.cardExpDate.value,
				cardholderName: validatedPaymentForm.cardholderName.value,
				cardZipCode: validatedPaymentForm.cardZipCode.value,
				gearOrderId: gearOrderId,
				cardAuthAmount: 15,
				fullPaymentAmount: 15
			};

			resolve(orderPayment);
		} else {
			reject('ValidationError');
		}
	});
};

/**
 * Because the PITL order has already been confirmed, some of the fields on the PatientInfo component are read-only.
 * We need to update the relevant fields to trick PatientInfo into being fully editable, but we need to keep the
 * original gearOrder intact in case the user cancels.
 */
const createEditableOrder = (gearOrder, gearReqIds) => {
	const editableOrder = window.structuredClone(gearOrder);

	// Set workflow complete=false to allow editing
	editableOrder.workflow.complete = false;

	// Clear the basic patient details (name, dob, etc.), but leave the address
	editableOrder.patientInfo.nameFirst = null;
	editableOrder.patientInfo.nameLast = null;
	editableOrder.patientInfo.nameMiddle = null;
	editableOrder.patientInfo.dateOfBirth = null;
	editableOrder.patientInfo.gender = null;
	editableOrder.patientInfo.driverLicenseNumber = null;
	editableOrder.patientInfo.ssn = null;
	editableOrder.patientInfo.race = null;
	editableOrder.patientInfo.ethnicity = null;
	editableOrder.patientInfo.responsibleNameFirst = null;
	editableOrder.patientInfo.responsibleNameMiddle = null;
	editableOrder.patientInfo.responsibleNameLast = null;
	editableOrder.patientInfo.occupation = null;
	editableOrder.patientInfo.employer = null;
	editableOrder.patientInfo.email = null;
	editableOrder.patientInfo.marketingOptIn = false;
	if (editableOrder.patientInfo.addresses?.[0].voicePhone) {
		editableOrder.patientInfo.addresses[0].voicePhone = null;
	}

	// We must remove any accessioned requisitions because accessioned packs lock the PatientInfo component
	editableOrder.requisitions = editableOrder.requisitions.filter((req) => gearReqIds.includes(req.id));

	return editableOrder;
};

const getPendingPayment = (gearOrder) => {
	return gearOrder.allPayments?.find((payment) => payment.paymentType === 'pitlpendingtransfer');
};

export default PITLTransfer;
